// Customers — Supabase implementation.
//
// Note the split of write rights, enforced in the database rather than here:
// a cashier may only change `email`. Name, phone, company and is_active are
// owner-only (guard_customer_update_scope), because invoices are addressed
// from these fields and a deactivation hides the customer from every till.

import { supabase } from '@/lib/supabase';
import { phoneKey, normalizePhone } from '@/lib/phone';
import { mapCustomer } from './mappers';
import { chunkIds, mapLimit, USAGE_CONCURRENCY, type BulkHardDeleteResult } from './bulk';
import type { Customer } from '@/types/domain';

export interface ListCustomersOptions { isActive?: boolean; max?: number; limit?: number }

export async function listCustomers(opts: ListCustomersOptions = {}): Promise<Customer[]> {
  let q = supabase
    .from('customers').select('*').order('name')
    .limit(opts.limit ?? opts.max ?? 500);
  if (opts.isActive !== undefined) q = q.eq('is_active', opts.isActive);
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return (data ?? []).map(mapCustomer);
}

export async function getCustomer(id: string): Promise<Customer | null> {
  const { data, error } = await supabase
    .from('customers').select('*').eq('id', id).maybeSingle();
  if (error) throw new Error(error.message);
  return data ? mapCustomer(data) : null;
}

export interface SearchCustomersOptions {
  term: string;
  max?: number;
  /** Ignored — the search runs server-side, so there is nothing to scan. */
  scanLimit?: number;
  /** Accepted for compatibility; the search is always active-only. */
  activeOnly?: boolean;
}

/**
 * Name-or-phone search for the POS picker. Server-side ILIKE, so the whole
 * customer table never has to reach the browser — the Firestore version
 * pulled a page and filtered locally.
 */
export async function searchCustomers(
  term: string,
  opts: Omit<SearchCustomersOptions, 'term'> = {},
): Promise<Customer[]> {
  const max = opts.max ?? 20;
  const t = term.trim();
  if (!t) return [];
  const safe = t.replace(/[%_,()]/g, ' ');
  const { data, error } = await supabase
    .from('customers')
    .select('*')
    .eq('is_active', true)
    .or(`name.ilike.%${safe}%,phone.ilike.%${safe}%,company.ilike.%${safe}%`)
    .order('name')
    .limit(max);
  if (error) throw new Error(error.message);
  return (data ?? []).map(mapCustomer);
}

export interface CreateCustomerInput {
  name: string;
  phone: string;
  company?: string | null;
  email?: string | null;
  /** NIK or NPWP — one field, see migration 0018. */
  taxId?: string | null;
  address?: string | null;
  /** Accepted but ignored — the actor comes from auth.uid() server-side. */
  createdBy?: string;
}

/** Returns the new customer's id. */
export async function createCustomer(input: CreateCustomerInput): Promise<string> {
  const { data, error } = await supabase
    .from('customers')
    .insert({
      name: input.name.trim(),
      phone: input.phone.trim(),
      company: input.company?.trim() || null,
      email: input.email?.trim() || null,
      tax_id: input.taxId?.trim() || null,
      address: input.address?.trim() || null,
    })
    .select('id')
    .single();
  if (error) throw new Error(error.message);
  return data.id;
}

export type UpdateCustomerInput = Partial<CreateCustomerInput>;

export async function updateCustomer(id: string, input: UpdateCustomerInput): Promise<void> {
  const patch: Record<string, unknown> = {};
  if (input.name !== undefined) patch.name = input.name.trim();
  if (input.phone !== undefined) patch.phone = input.phone.trim();
  if (input.company !== undefined) patch.company = input.company?.trim() || null;
  if (input.email !== undefined) patch.email = input.email?.trim() || null;
  if (input.taxId !== undefined) patch.tax_id = input.taxId?.trim() || null;
  if (input.address !== undefined) patch.address = input.address?.trim() || null;
  const { error } = await supabase.from('customers').update(patch as never).eq('id', id);
  if (error) {
    // Raised by guard_customer_update_scope for a cashier touching a
    // protected column — pass the message straight through, it is readable.
    throw new Error(error.message);
  }
}

export async function setCustomerActive(
  idOrCustomer: string | { id: string },
  isActive: boolean,
): Promise<void> {
  const id = typeof idOrCustomer === 'string' ? idOrCustomer : idOrCustomer.id;
  const { error } = await supabase.from('customers').update({ is_active: isActive }).eq('id', id);
  if (error) throw new Error(error.message);
}

/** Soft delete: invoices snapshot the customer, but the row must survive. */
export const deleteCustomer = (id: string | { id: string }) => setCustomerActive(id, false);

// ── Bulk import ──────────────────────────────────────────────────────────────

/**
 * Phone reduced to the form used for comparison only — never for storage.
 *
 * "0811 6060 0518", "+62 811-6060-0518" and "628116060518" are one customer
 * written three ways, and a spreadsheet will contain all three. Comparing the
 * raw strings would import the same person repeatedly; the stored value stays
 * exactly as typed so nothing is silently rewritten.
 */
export function normalizePhoneKey(raw: string): string {
  // Delegates to lib/phone so the duplicate check, the importer and the form
  // all agree on what "the same number" means. They used to each have their
  // own copy of the 0/62 rules and drift was only a matter of time.
  return phoneKey(raw);
}

/**
 * Every customer's phone, in comparison form, inactive ones included.
 *
 * Paged deliberately: listCustomers() caps at 500, and using it here would mean
 * that on the 501st customer the duplicate check silently stops working and the
 * import starts creating second copies of people. Inactive customers count too
 * — importing a duplicate of someone who was deactivated on purpose is exactly
 * the mess this is meant to prevent.
 */
async function fetchAllPhoneKeys(): Promise<string[]> {
  const PAGE = 1000;
  const keys: string[] = [];
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await supabase
      .from('customers')
      .select('phone')
      .order('id')
      .range(from, from + PAGE - 1);
    if (error) throw new Error(error.message);
    const batch = data ?? [];
    for (const r of batch) keys.push(normalizePhoneKey(r.phone));
    if (batch.length < PAGE) break;
  }
  return keys;
}

export interface ImportCustomerRow {
  name: string;
  taxId?: string;
  phone: string;
  address?: string;
}

export interface ImportOutcome {
  imported: number;
  /** Rows whose phone already existed. Never touched — see the import dialog. */
  skippedExisting: number;
  failed: { row: ImportCustomerRow; reason: string }[];
}

/**
 * Insert only the rows whose phone is not already on the books.
 *
 * Existing customers are left completely alone: an import file is a snapshot of
 * someone's spreadsheet, and a blank cell in it must never be able to erase a
 * phone number or address that is already correct.
 *
 * Inserted in chunks rather than one statement so a single malformed row does
 * not cost the whole file, and the caller can report exactly which rows failed.
 */
export async function importCustomers(rows: ImportCustomerRow[]): Promise<ImportOutcome> {
  const seen = new Set(await fetchAllPhoneKeys());

  const out: ImportOutcome = { imported: 0, skippedExisting: 0, failed: [] };
  const toInsert: { row: ImportCustomerRow; payload: Record<string, unknown> }[] = [];

  for (const row of rows) {
    const key = normalizePhoneKey(row.phone);
    if (!row.name?.trim()) { out.failed.push({ row, reason: 'Nama kosong' }); continue; }
    if (!key)              { out.failed.push({ row, reason: 'Nomor telepon kosong / tidak valid' }); continue; }
    // Also catches duplicates *within* the pasted file, not just against the
    // database — the same number twice in one paste is the commoner mistake.
    if (seen.has(key))     { out.skippedExisting += 1; continue; }
    seen.add(key);
    toInsert.push({
      row,
      payload: {
        name: row.name.trim(),
        // Stored canonical, exactly like the form does. A spreadsheet full of
        // "0811…" and "+62 811-…" must not become two shapes in one table.
        phone: normalizePhone(row.phone) ?? row.phone.trim(),
        tax_id: row.taxId?.trim() || null,
        address: row.address?.trim() || null,
      },
    });
  }

  const CHUNK = 100;
  for (let i = 0; i < toInsert.length; i += CHUNK) {
    const slice = toInsert.slice(i, i + CHUNK);
    const { error } = await supabase.from('customers').insert(slice.map((s) => s.payload) as never);
    if (!error) {
      out.imported += slice.length;
      continue;
    }
    // The chunk failed as a unit, so retry singly to find which rows are bad
    // rather than blaming all hundred of them.
    for (const one of slice) {
      const { error: e1 } = await supabase.from('customers').insert(one.payload as never);
      if (e1) out.failed.push({ row: one.row, reason: e1.message });
      else out.imported += 1;
    }
  }
  return out;
}

/**
 * Bulk deactivate or reactivate, for the checkbox selection on the Customers
 * page. Chunked for the same reason as products: the ids ride in the query
 * string.
 *
 * is_active is owner-only and the database is what enforces it
 * (guard_customer_update_scope), not this function. A cashier who reaches here
 * is refused on the first chunk and the guard's own message is passed through,
 * so the reason they see is the real one.
 */
export async function setCustomersActive(ids: string[], isActive: boolean): Promise<number> {
  const chunks = chunkIds(ids);
  const total = chunks.reduce((n, c) => n + c.length, 0);
  let done = 0;
  for (const chunk of chunks) {
    // .select() returns the rows actually changed. Without it, rows the
    // caller may not update (RLS) are skipped silently and the request still
    // "succeeds" — the screen would say done while nothing changed.
    const { data, error } = await supabase
      .from('customers').update({ is_active: isActive }).in('id', chunk).select('id');
    if (error) {
      throw new Error(`${done} dari ${total} pelanggan diproses, lalu gagal: ${error.message}`);
    }
    const changed = data?.length ?? 0;
    done += changed;
    if (changed < chunk.length) {
      throw new Error(
        `${done} dari ${total} pelanggan diproses; ${chunk.length - changed} tidak bisa diubah — kemungkinan akun ini tidak punya hak untuk itu.`,
      );
    }
  }
  return done;
}

export interface CustomerUsage {
  transactions: number;
  payments: number;
}

/**
 * How many rows reference this customer. Zero everywhere = never used.
 *
 * transaction_drafts is not counted, on purpose. RLS keeps a cashier's draft
 * private even from the owner, so any count would be incomplete by design; its
 * customer_id is ON DELETE SET NULL, so missing one costs a cashier re-picking
 * the customer on an unfinished cart, not a damaged record. A pending
 * customer.edit approval is not counted either: its apply step raises
 * "Customer pada permintaan ini sudah tidak ada" instead of writing anywhere.
 */
export async function getCustomerUsage(id: string): Promise<CustomerUsage> {
  const countIn = async (table: 'transactions' | 'payments') => {
    const { count, error } = await supabase
      .from(table).select('*', { count: 'exact', head: true }).eq('customer_id', id);
    if (error) throw new Error(error.message);
    return count ?? 0;
  };
  const [transactions, payments] = await Promise.all([
    countIn('transactions'),
    countIn('payments'),
  ]);
  return { transactions, payments };
}

export const isCustomerUsed = (u: CustomerUsage) => u.transactions + u.payments > 0;

/**
 * Bulk permanent delete -- of the customers nothing references, and only
 * those. Same shape as hardDeleteUnusedProducts and for the same reasons:
 * exact per-customer counts first, and the removed rows asked back because RLS
 * on DELETE refuses by matching nothing.
 *
 * What it protects: transactions.customer_id and payments.customer_id are ON
 * DELETE SET NULL, so the database would let a customer with sales go and leave
 * their invoices and payments belonging to nobody -- out of reach of piutang,
 * which looks them up by customer_id. Nothing references customers with
 * RESTRICT, so unlike products there is no foreign-key refusal behind this:
 * the count is the only guard.
 */
export async function hardDeleteUnusedCustomers(ids: string[]): Promise<BulkHardDeleteResult> {
  const unique = [...new Set(ids)].filter(Boolean);
  const checked = await mapLimit(unique, USAGE_CONCURRENCY, async (id) => ({
    id,
    used: isCustomerUsed(await getCustomerUsage(id)),
  }));
  const inUse = checked.filter((c) => c.used).map((c) => c.id);
  const free = checked.filter((c) => !c.used).map((c) => c.id);

  const deleted: string[] = [];
  for (const chunk of chunkIds(free)) {
    const { data, error } = await supabase.from('customers').delete().in('id', chunk).select('id');
    if (error) {
      throw new Error(
        `${deleted.length} dari ${free.length} customer sudah terhapus, lalu gagal: ${error.message}`,
      );
    }
    for (const r of data ?? []) deleted.push(r.id);
  }

  const gone = new Set(deleted);
  return { deleted, inUse, untouched: free.filter((id) => !gone.has(id)) };
}
